import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useState } from "react";
import Confetti from "react-confetti";

import {
  getGuaranteedPayWaitlistStorageKey,
  GUARANTEED_PAY_WAITLIST_RESPONSES,
  type GuaranteedPayWaitlistResponse,
} from "./constants";
import { GuaranteedPayWaitlistBanner } from "./GuaranteedPayWaitlistBanner";
import { GuaranteedPayWaitlistBottomSheet } from "./GuaranteedPayWaitlistBottomSheet";

export function GuaranteedPayWaitlistEntrypoint() {
  const bottomSheetModalState = useModalState();
  const { userId } = useDefinedWorker();
  const { showInfoToast } = useToast();
  const [showConfetti, setShowConfetti] = useState(false);

  const isEnabled = useCbhFlag(CbhFeatureFlag.GUARANTEED_PAY_WAITLIST, {
    defaultValue: false,
  });

  const storageKey = getGuaranteedPayWaitlistStorageKey(userId);

  const [waitlistResponse, setWaitlistResponse] = useLocalStorage<
    GuaranteedPayWaitlistResponse | undefined
  >(storageKey, undefined);

  const shouldShowBanner = isEnabled && !isDefined(waitlistResponse);

  useLogEffect(
    APP_V2_USER_EVENTS.GUARANTEED_PAY_WAITLIST_BANNER_VIEWED,
    { workerId: userId },
    { enabled: shouldShowBanner }
  );

  const handleBannerClick = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.GUARANTEED_PAY_WAITLIST_BANNER_CTA_CLICKED, {
      workerId: userId,
    });
    bottomSheetModalState.openModal();
  }, [userId, bottomSheetModalState]);

  const handleSubmit = useCallback(
    (response: GuaranteedPayWaitlistResponse) => {
      setWaitlistResponse(response);

      if (response === GUARANTEED_PAY_WAITLIST_RESPONSES.INTERESTED) {
        showInfoToast("You're in! Guaranteed Pay is on the way.");
        setShowConfetti(true);
      } else {
        showInfoToast("Thanks for your feedback on Guaranteed Pay!");
      }
    },
    [setWaitlistResponse, showInfoToast]
  );

  if (!shouldShowBanner && !showConfetti) {
    return null;
  }

  return (
    <>
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={400}
          gravity={0.8}
          recycle={false}
          onConfettiComplete={() => {
            setShowConfetti(false);
          }}
        />
      )}
      {shouldShowBanner && (
        <>
          <GuaranteedPayWaitlistBanner onClick={handleBannerClick} />
          <GuaranteedPayWaitlistBottomSheet
            modalState={bottomSheetModalState}
            workerId={userId}
            onSubmit={handleSubmit}
          />
        </>
      )}
    </>
  );
}
